import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import DotWaveLoader from '../commonComponents/DotWaveLoader';
import {
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { compactIconButtonSx, compactPanelPaddingSx, compactSelectSx } from './tableStyles';
import CompactPanelHeader from '../commonComponents/CompactPanelHeader';
import { cardClasses } from '../../styles/tailwindClasses';
import { buildScatterPalette } from '../../styles/themeTokens';
import { useAppContext } from '../../context/AppContext';
import {
    SECURITY_IDENTITY_FIELD_OPTIONS,
    securityCompanyName,
    securitySubmittedIdentifier,
    securityTickerPresentation,
} from '../../utils/securityIdentityPresentation';
import {
    analyticsInsetPanelSx,
    analyticsPanelSx,
    analyticsTooltipSx,
    getAnalyticsChartChrome,
} from '../../styles/analyticsMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS,
    MSI_GEOMETRY,
} from '../../styles/msiStyleTokens';
import { isWatchlistGroupingAttribute } from '../../utils/watchlistAttributePresentation';

const DEFAULT_POINT_OPACITY = 0.46;
const DRAG_THRESHOLD_PX = 6;
const CHART_MARGIN = { top: 12, right: 20, bottom: 16, left: 6 };

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
});
const fullNumberFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
});
const fullDollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
});

function toFiniteNumber(value) {
    if (value == null) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function formatCompactNumber(value) {
    const numericValue = toFiniteNumber(value);
    if (numericValue == null) return '-';
    if (Math.abs(numericValue) >= 1000) {
        return compactNumberFormatter.format(numericValue);
    }
    return fullNumberFormatter.format(numericValue);
}

function formatCompactDollar(value) {
    const numericValue = toFiniteNumber(value);
    if (numericValue == null) return '-';
    const sign = numericValue < 0 ? '-' : '';
    return `${sign}$${compactNumberFormatter.format(Math.abs(numericValue))}`;
}

function formatTooltipNumber(value) {
    const numericValue = toFiniteNumber(value);
    if (numericValue == null) return '-';
    return fullNumberFormatter.format(numericValue);
}

function formatTooltipDollar(value) {
    const numericValue = toFiniteNumber(value);
    if (numericValue == null) return '-';
    return fullDollarFormatter.format(numericValue);
}

function buildSymmetricDomain(values) {
    const maxAbs = values.reduce((largest, value) => {
        const numericValue = toFiniteNumber(value);
        return numericValue == null ? largest : Math.max(largest, Math.abs(numericValue));
    }, 0);

    if (maxAbs === 0) {
        return [-1, 1];
    }

    const paddedMax = maxAbs * 1.08;
    return [-paddedMax, paddedMax];
}

function ScatterTooltip({ active, payload }) {
    const point = payload?.[0]?.payload;
    if (!active || !point) return null;

    const tooltipRows = [
        ['Company name', point.companyName],
        ['Ticker', point.tickerDisplay],
        ['Uploaded identifier', point.identifierLabel || '—'],
        ['Trade idea', formatTooltipNumber(point.tradeIdea)],
        ['GMV', formatTooltipDollar(point.gmv)],
        ...(point.attributeLabel
            ? [[point.attributeLabel, point.attributeValue || 'Unassigned']]
            : []),
        ['Price', formatTooltipDollar(point.price)],
        ['ADTV', point.adtv == null ? '-' : formatTooltipDollar(point.adtv)],
    ];

    return (
        <Box
            sx={[{
                minWidth: 220,
                border: theme => `1px solid ${alpha(theme.palette.divider, 0.95)}`,
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                boxShadow: theme => theme.shadows[6],
                px: 1.25,
                py: 1,
            }, analyticsTooltipSx]}
        >
            {tooltipRows.map(([label, value]) => (
                <Box
                    key={label}
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 1.25,
                        py: 0.2,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {label}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, textAlign: 'right', color: 'text.primary' }}
                    >
                        {value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

ScatterTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
        payload: PropTypes.object,
    })),
};

export default function PosSizingScatterPlot({
    rows,
    active = true,
    loading = false,
    selectedPointId = null,
    onPointSelect,
    attributeSnapshot = null,
    defaultAttributeDefinitionId = null,
    minHeight = 540,
}) {
    const theme = useTheme();
    const { sectorPalette } = useAppContext();
    const scatterPalette = useMemo(() => buildScatterPalette(sectorPalette), [sectorPalette]);
    const [selectedAttributeDefinitionId, setSelectedAttributeDefinitionId] = useState('');
    const [selectedGroups, setSelectedGroups] = useState(null);
    const [pointLabelField, setPointLabelField] = useState('ticker');
    const [zoomDomain, setZoomDomain] = useState(null);
    const [dragBounds, setDragBounds] = useState(null);
    const pendingDragStartRef = useRef(null);

    const attributeDefinitions = useMemo(() => (
        (Array.isArray(attributeSnapshot?.definitions)
            ? attributeSnapshot.definitions
            : []
        ).filter(isWatchlistGroupingAttribute)
    ), [attributeSnapshot]);
    const availableAttributeDefinitionIds = useMemo(
        () => new Set(attributeDefinitions.map(
            (definition) => String(definition.attribute_definition_id),
        )),
        [attributeDefinitions],
    );
    const preferredAttributeDefinitionId = String(defaultAttributeDefinitionId || '');
    const fallbackAttributeDefinitionId = availableAttributeDefinitionIds.has(
        preferredAttributeDefinitionId,
    )
        ? preferredAttributeDefinitionId
        : String(attributeDefinitions[0]?.attribute_definition_id || '');
    const activeAttributeDefinitionId = availableAttributeDefinitionIds.has(
        selectedAttributeDefinitionId,
    )
        ? selectedAttributeDefinitionId
        : fallbackAttributeDefinitionId;
    const activeAttributeDefinition = attributeDefinitions.find(
        (definition) => (
            String(definition.attribute_definition_id) === activeAttributeDefinitionId
        ),
    ) ?? null;
    const activeAttributeValues = useMemo(() => new Map(
        (Array.isArray(attributeSnapshot?.values) ? attributeSnapshot.values : [])
            .filter((value) => (
                String(value?.attribute_definition_id || '')
                === activeAttributeDefinitionId
            ))
            .map((value) => [String(value.cognive_id), String(value.value_text || '').trim()]),
    ), [activeAttributeDefinitionId, attributeSnapshot]);

    const allChartData = useMemo(
        () =>
            (rows || [])
                .filter(row => !row?.toDelete)
                .map(row => {
                    const tradeIdea = toFiniteNumber(row?.trade_idea);
                    const gmv =
                        toFiniteNumber(row?.pos_gmv_dollar_precise) ??
                        toFiniteNumber(row?.pos_gmv_dollar);

                    if (tradeIdea == null || gmv == null) {
                        return null;
                    }

                    const attributeValue = activeAttributeValues.get(
                        String(row?.cognive_id || ''),
                    ) ?? '';
                    return {
                        id: row.id,
                        sedol: row?.sedol ?? '',
                        identifierLabel: securitySubmittedIdentifier(row, { includeTickerType: true }),
                        submittedIdentifierLabel: securitySubmittedIdentifier(row),
                        companyName: securityCompanyName(row),
                        tickerDisplay: securityTickerPresentation(row).value,
                        ticker: row?.ticker ?? '',
                        tradeIdea,
                        gmv,
                        attributeLabel: activeAttributeDefinition?.display_name ?? '',
                        attributeValue,
                        price: toFiniteNumber(row?.price),
                        adtv: toFiniteNumber(row?.dollar_adtv),
                    };
                })
                .filter(Boolean),
        [activeAttributeDefinition, activeAttributeValues, rows]
    );

    const sortedGroups = useMemo(
        () => Array.from(new Set(
            allChartData.map(point => point.attributeValue).filter(Boolean),
        )).sort((leftGroup, rightGroup) =>
            leftGroup.localeCompare(rightGroup)
        ),
        [allChartData]
    );

    const groupColorMap = useMemo(() => {
        const colorMap = new Map();
        sortedGroups.forEach((group, index) => {
            colorMap.set(group, scatterPalette[index % scatterPalette.length]);
        });
        return colorMap;
    }, [scatterPalette, sortedGroups]);

    const legendItems = useMemo(
        () => sortedGroups.map(group => [group, groupColorMap.get(group)]),
        [groupColorMap, sortedGroups]
    );

    const availableGroups = useMemo(
        () => legendItems.map(([group]) => group),
        [legendItems]
    );

    useEffect(() => {
        if (selectedGroups == null) return;

        const availableGroupSet = new Set(availableGroups);
        const nextSelectedGroups = selectedGroups.filter(group => availableGroupSet.has(group));

        if (
            nextSelectedGroups.length === 0 ||
            nextSelectedGroups.length === availableGroups.length
        ) {
            setSelectedGroups(null);
            return;
        }

        if (nextSelectedGroups.length !== selectedGroups.length) {
            setSelectedGroups(nextSelectedGroups);
        }
    }, [availableGroups, selectedGroups]);

    useEffect(() => {
        setZoomDomain(null);
        setDragBounds(null);
        pendingDragStartRef.current = null;
    }, [selectedGroups]);

    const filteredChartData = useMemo(() => {
        if (selectedGroups == null) {
            return allChartData.map(point => ({
                ...point,
                color: groupColorMap.get(point.attributeValue) ?? theme.palette.grey[500],
            }));
        }

        const activeGroupSet = new Set(selectedGroups);
        return allChartData
            .filter(point => activeGroupSet.has(point.attributeValue))
            .map(point => ({
                ...point,
                color: groupColorMap.get(point.attributeValue) ?? theme.palette.grey[500],
            }));
    }, [allChartData, groupColorMap, selectedGroups, theme.palette.grey]);

    const baseXDomain = useMemo(
        () => buildSymmetricDomain(filteredChartData.map(point => point.tradeIdea)),
        [filteredChartData]
    );
    const baseYDomain = useMemo(
        () => buildSymmetricDomain(filteredChartData.map(point => point.gmv)),
        [filteredChartData]
    );

    const xDomain = zoomDomain?.x ?? baseXDomain;
    const yDomain = zoomDomain?.y ?? baseYDomain;

    const handleScatterClick = useCallback(
        point => {
            const payload = point?.payload ?? point;
            if (payload?.id) {
                onPointSelect?.(payload.id);
            }
        },
        [onPointSelect]
    );

    const handleShowAllGroups = useCallback(() => {
        setSelectedGroups(null);
    }, []);

    const handleGroupToggle = useCallback(
        group => {
            setSelectedGroups(currentSelection => {
                if (currentSelection == null) {
                    return [group];
                }

                if (currentSelection.includes(group)) {
                    if (currentSelection.length === 1) {
                        return null;
                    }

                    return currentSelection.filter(item => item !== group);
                }

                const nextSelection = [...currentSelection, group].sort((leftGroup, rightGroup) =>
                    leftGroup.localeCompare(rightGroup)
                );

                return nextSelection.length === availableGroups.length ? null : nextSelection;
            });
        },
        [availableGroups]
    );

    const handleAttributeChange = useCallback((event) => {
        setSelectedAttributeDefinitionId(String(event.target.value));
        setSelectedGroups(null);
        setZoomDomain(null);
        setDragBounds(null);
        pendingDragStartRef.current = null;
    }, []);

    const handleChartMouseDown = useCallback(eventState => {
        const xValue = toFiniteNumber(eventState?.xValue);
        const yValue = toFiniteNumber(eventState?.yValue);
        const chartX = toFiniteNumber(eventState?.chartX);
        const chartY = toFiniteNumber(eventState?.chartY);

        if (xValue == null || yValue == null || chartX == null || chartY == null) {
            return;
        }

        pendingDragStartRef.current = {
            chartX,
            chartY,
            x1: xValue,
            y1: yValue,
        };
    }, []);

    const handleChartMouseMove = useCallback(eventState => {
        const xValue = toFiniteNumber(eventState?.xValue);
        const yValue = toFiniteNumber(eventState?.yValue);
        const chartX = toFiniteNumber(eventState?.chartX);
        const chartY = toFiniteNumber(eventState?.chartY);

        if (xValue == null || yValue == null || chartX == null || chartY == null) {
            return;
        }

        const pendingDragStart = pendingDragStartRef.current;
        if (pendingDragStart == null) {
            return;
        }

        if (dragBounds == null) {
            const deltaX = Math.abs(chartX - pendingDragStart.chartX);
            const deltaY = Math.abs(chartY - pendingDragStart.chartY);
            if (deltaX < DRAG_THRESHOLD_PX && deltaY < DRAG_THRESHOLD_PX) {
                return;
            }

            setDragBounds({
                x1: pendingDragStart.x1,
                y1: pendingDragStart.y1,
                x2: xValue,
                y2: yValue,
            });
            return;
        }

        setDragBounds(currentBounds => (
            currentBounds == null
                ? currentBounds
                : {
                    ...currentBounds,
                    x2: xValue,
                    y2: yValue,
                }
        ));
    }, [dragBounds]);

    const handleResetZoom = useCallback(() => {
        setZoomDomain(null);
        setDragBounds(null);
        pendingDragStartRef.current = null;
    }, []);

    const handleChartMouseUp = useCallback(
        (eventState) => {
            const pendingDragStart = pendingDragStartRef.current;
            pendingDragStartRef.current = null;
            if (!dragBounds || !pendingDragStart) {
                setDragBounds(null);
                return;
            }

            const x2 = toFiniteNumber(eventState?.xValue) ?? dragBounds.x2;
            const y2 = toFiniteNumber(eventState?.yValue) ?? dragBounds.y2;
            const nextBounds = {
                x1: pendingDragStart.x1,
                y1: pendingDragStart.y1,
                x2,
                y2,
            };
            setDragBounds(null);

            const xSpan = Math.abs(nextBounds.x2 - nextBounds.x1);
            const ySpan = Math.abs(nextBounds.y2 - nextBounds.y1);
            const minXSpan = Math.abs(xDomain[1] - xDomain[0]) * 0.03;
            const minYSpan = Math.abs(yDomain[1] - yDomain[0]) * 0.03;
            if (xSpan < minXSpan || ySpan < minYSpan) {
                return;
            }

            setZoomDomain({
                x: [Math.min(nextBounds.x1, nextBounds.x2), Math.max(nextBounds.x1, nextBounds.x2)],
                y: [Math.min(nextBounds.y1, nextBounds.y2), Math.max(nextBounds.y1, nextBounds.y2)],
            });
        },
        [dragBounds, xDomain, yDomain]
    );

    const renderPointShape = useCallback(
        ({ cx, cy, payload }) => {
            if (cx == null || cy == null || !payload) return null;
            const isSelected = payload.id === selectedPointId;
            const pointLabel = {
                company_name: payload.companyName,
                ticker: payload.tickerDisplay,
                submitted_identifier: payload.submittedIdentifierLabel,
            }[pointLabelField];

            return (
                <g>
                    <circle
                        cx={cx}
                        cy={cy}
                        r={isSelected ? 6.5 : 5}
                        fill={payload.color}
                        fillOpacity={isSelected ? 0.98 : DEFAULT_POINT_OPACITY}
                        stroke={
                            isSelected
                                ? theme.palette.text.primary
                                : alpha(payload.color, 0.42)
                        }
                        strokeWidth={isSelected ? 2.25 : 1.15}
                        style={{ cursor: 'pointer' }}
                    />
                    {pointLabel && pointLabel !== '—' ? (
                        <text
                            x={cx + (isSelected ? 9 : 8)}
                            y={cy - 8}
                            fill={theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary}
                            fontSize="10"
                            fontWeight="600"
                            pointerEvents="none"
                            paintOrder="stroke"
                            stroke={theme.palette.background.paper}
                            strokeWidth="3"
                            strokeLinejoin="round"
                        >
                            {pointLabel}
                        </text>
                    ) : null}
                </g>
            );
        },
        [pointLabelField, selectedPointId, theme]
    );

    const noChartableData = allChartData.length === 0;
    const noFilteredData = !noChartableData && filteredChartData.length === 0;
    const contentMinHeight = Math.max(240, minHeight - 92);
    const chartChrome = getAnalyticsChartChrome(theme);
    const chartTextColor = chartChrome.text;
    const chartGridColor = chartChrome.grid;
    const chartAxisColor = chartChrome.axis;
    const chartReferenceColor = chartChrome.reference;

    return (
        <Paper
            className={cardClasses}
            elevation={1}
            sx={[{
                minHeight,
                height: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                ...compactPanelPaddingSx,
            }, analyticsPanelSx]}
        >
            <CompactPanelHeader
                title="Positioning Map"
                description="Compare trade ideas with gross market value and attribute groups."
            >
                <FormControl size="small" sx={{ minWidth: 150, ...compactSelectSx }}>
                    <InputLabel id="positioning-map-point-label">Point labels</InputLabel>
                    <Select
                        labelId="positioning-map-point-label"
                        label="Point labels"
                        value={pointLabelField}
                        onChange={(event) => setPointLabelField(event.target.value)}
                    >
                        {SECURITY_IDENTITY_FIELD_OPTIONS.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                        <MenuItem value="none">None</MenuItem>
                    </Select>
                </FormControl>
                <Tooltip title="Reset view">
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleResetZoom}
                            disabled={zoomDomain == null && dragBounds == null}
                            aria-label="Reset view"
                            sx={compactIconButtonSx}
                        >
                            <RestartAltRoundedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </CompactPanelHeader>

            <Box sx={{ position: 'relative', flex: 1, minHeight: contentMinHeight + 64, display: 'flex', flexDirection: 'column' }}>
                {/* Preserve the plot and legend's layout while the canvas is an
                    absolute overlay, so its measured bitmap cannot resize the card. */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', visibility: loading ? 'hidden' : 'visible' }}>
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minHeight: contentMinHeight,
                            cursor: 'crosshair',
                            userSelect: 'none',
                        }}
                    >
                        {/* Let the panel size the chart, without the chart's measured
                            pixel height feeding back into the auto-sized grid row.
                            Inactive persistent views have no chart dimensions. */}
                        {active && !loading ? <ResponsiveContainer
                            width="100%"
                            height="100%"
                            style={{ position: 'absolute', inset: 0 }}
                        >
                            <ScatterChart
                                margin={CHART_MARGIN}
                                onMouseDown={handleChartMouseDown}
                                onMouseMove={handleChartMouseMove}
                                onMouseUp={handleChartMouseUp}
                                onMouseLeave={() => {
                                    pendingDragStartRef.current = null;
                                    setDragBounds(null);
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                <XAxis
                                    type="number"
                                    dataKey="tradeIdea"
                                    domain={xDomain}
                                    allowDataOverflow
                                    label={{
                                        value: 'Trade Idea',
                                        position: 'insideBottom',
                                        offset: -4,
                                        fill: chartTextColor,
                                        style: { fontSize: chartChrome.tickFontSize, fontWeight: 600 },
                                    }}
                                    tickFormatter={formatCompactNumber}
                                    tick={{ fill: chartTextColor, fontSize: chartChrome.tickFontSize }}
                                    axisLine={{ stroke: chartAxisColor }}
                                    tickLine={{ stroke: chartAxisColor }}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="gmv"
                                    domain={yDomain}
                                    allowDataOverflow
                                    width={82}
                                    label={{
                                        value: 'GMV',
                                        angle: -90,
                                        position: 'insideLeft',
                                        fill: chartTextColor,
                                        dx: 8,
                                        style: { textAnchor: 'middle', fontSize: chartChrome.tickFontSize, fontWeight: 600 },
                                    }}
                                    tickFormatter={formatCompactDollar}
                                    tick={{ fill: chartTextColor, fontSize: chartChrome.tickFontSize }}
                                    axisLine={{ stroke: chartAxisColor }}
                                    tickLine={{ stroke: chartAxisColor }}
                                />
                                <ReferenceLine x={0} stroke={chartReferenceColor} strokeDasharray="4 4" />
                                <ReferenceLine y={0} stroke={chartReferenceColor} strokeDasharray="4 4" />
                                {dragBounds ? (
                                    <ReferenceArea
                                        x1={dragBounds.x1}
                                        x2={dragBounds.x2}
                                        y1={dragBounds.y1}
                                        y2={dragBounds.y2}
                                        stroke={theme.palette.primary.main}
                                        fill={theme.palette.primary.main}
                                        fillOpacity={0.08}
                                    />
                                ) : null}
                                <RechartsTooltip
                                    cursor={{ stroke: alpha(theme.palette.text.primary, 0.2), strokeDasharray: '4 4' }}
                                    content={<ScatterTooltip />}
                                />
                                <Scatter
                                    data={filteredChartData}
                                    shape={renderPointShape}
                                    isAnimationActive={false}
                                    onClick={handleScatterClick}
                                />
                            </ScatterChart>
                        </ResponsiveContainer> : null}
                        {(noChartableData || noFilteredData) && !loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    px: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={[{
                                        textAlign: 'center',
                                        maxWidth: 360,
                                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
                                        px: 1.1,
                                        py: 0.6,
                                        borderRadius: 1.5,
                                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                                    }, analyticsInsetPanelSx]}
                                >
                                    {noChartableData
                                        ? 'No chartable positions yet. Add trade idea and GMV values to plot the current draft.'
                                        : `No positions match the current ${String(
                                            activeAttributeDefinition?.display_name || 'attribute',
                                        ).toLowerCase()} filter. Use All to reset.`}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.8,
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: theme => `1px solid ${alpha(theme.palette.divider, 0.75)}`,
                        }}
                    >
                        {attributeDefinitions.length > 0 ? (
                            <>
                                <FormControl size="small" sx={{ minWidth: 170, mr: 0.2 }}>
                                    <InputLabel id="positioning-map-attribute-label">
                                        Map attribute
                                    </InputLabel>
                                    <Select
                                        labelId="positioning-map-attribute-label"
                                        label="Map attribute"
                                        value={activeAttributeDefinitionId}
                                        onChange={handleAttributeChange}
                                    >
                                        {attributeDefinitions.map((definition) => (
                                            <MenuItem
                                                key={definition.attribute_definition_id}
                                                value={String(definition.attribute_definition_id)}
                                            >
                                                {definition.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {legendItems.length > 0 ? (
                                    <>
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={handleShowAllGroups}
                                            sx={{
                                                cursor: 'pointer',
                                                border: 'none',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.6,
                                                px: 0.9,
                                                py: 0.35,
                                                borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                color: selectedGroups == null
                                                    ? 'primary.main'
                                                    : 'text.secondary',
                                                backgroundColor:
                                                    selectedGroups == null
                                                        ? alpha(theme.palette.primary.main, 0.12)
                                                        : alpha(theme.palette.text.secondary, 0.08),
                                                '&:focus-visible': {
                                                    outline: `2px solid ${APP_STYLE_COMPONENT_COLORS.focusBorder}`,
                                                    outlineOffset: 2,
                                                },
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                All {activeAttributeDefinition?.display_name || 'Values'}
                                            </Typography>
                                        </Box>
                                        {legendItems.map(([group, color]) => {
                                            const isActive = selectedGroups == null
                                                || selectedGroups.includes(group);

                                            return (
                                                <Box
                                                    key={group}
                                                    component="button"
                                                    type="button"
                                                    onClick={() => handleGroupToggle(group)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        border: 'none',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.6,
                                                        px: 0.8,
                                                        py: 0.25,
                                                        borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                        opacity: isActive ? 1 : 0.48,
                                                        color: 'text.secondary',
                                                        backgroundColor: theme => alpha(
                                                            color,
                                                            isActive ? 0.12 : 0.05,
                                                        ),
                                                        outline: isActive
                                                            ? `1px solid ${alpha(color, 0.38)}`
                                                            : `1px solid ${alpha(
                                                                theme.palette.divider,
                                                                0.35,
                                                            )}`,
                                                        '&:focus-visible': {
                                                            outline: `2px solid ${APP_STYLE_COMPONENT_COLORS.focusBorder}`,
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            backgroundColor: color,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography variant="caption">
                                                        {group}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        No values for this attribute are present in the current draft.
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Watchlist attributes will appear here after positions are loaded.
                            </Typography>
                        )}
                    </Box>
                </Box>
                {loading ? (
                    <DotWaveLoader height="100%" label="Loading positioning map" sx={{ position: 'absolute', inset: 0 }} />
                ) : null}
            </Box>
        </Paper>
    );
}

PosSizingScatterPlot.propTypes = {
    active: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        ticker: PropTypes.string,
        cognive_id: PropTypes.string,
        sedol: PropTypes.string,
        inbound_id_type: PropTypes.string,
        inbound_id_value: PropTypes.string,
        trade_idea: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pos_gmv_dollar: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pos_gmv_dollar_precise: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dollar_adtv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        toDelete: PropTypes.bool,
    })),
    loading: PropTypes.bool,
    selectedPointId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPointSelect: PropTypes.func,
    attributeSnapshot: PropTypes.shape({
        definitions: PropTypes.arrayOf(PropTypes.object).isRequired,
        values: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
    defaultAttributeDefinitionId: PropTypes.string,
    minHeight: PropTypes.number,
};
